<template>
  <div class="signature-mode-step">
    <!-- Signature Levels -->
    <section class="signature-mode-step__section">
      <div
        :aria-label="i18n.t('titles.signature_mode_step')"
        class="signature-level-list"
        role="radiogroup"
      >
        <!-- One card per level. The tooltip wrapper is always rendered but only
             enabled for unavailable levels, so the card markup lives in a single
             place instead of being duplicated for the disabled variant. -->
        <v-tooltip
          v-for="(level, index) in availableSignatureModes"
          :key="level.key"
          :disabled="!level.disabled"
          location="bottom"
          offset="4"
          open-delay="300"
          open-on-hover
          :text="
            !hasPaymentMethodSetup
              ? i18n.t('texts.no_payment_method_setup')
              : i18n.t('texts.unavailable_signature_mode')
          "
        >
          <template #activator="{ props: tooltipProps }">
            <!-- The tooltip binds to this wrapper, not the card: the card drops
                 pointer events while disabled and would never show the hint. -->
            <div v-bind="tooltipProps" class="signature-level-list__item">
              <!-- Hand-rolled radio (rather than v-radio) so the group keeps the
                   full ARIA radiogroup contract — roving tabindex, arrow-key
                   selection that skips unavailable levels — and so the whole
                   card, not just a label, is the control. -->
              <div
                :ref="(el) => setLevelRef(el, index)"
                :aria-checked="level.key === signatureMode"
                :aria-disabled="level.disabled || undefined"
                class="signature-level-card"
                :class="{
                  'signature-level-card--selected':
                    level.key === signatureMode && !level.disabled,
                  'signature-level-card--disabled': level.disabled
                }"
                :data-test-id="`signature-${level.dataTestId}`"
                :data-test-is-selected="level.key === signatureMode"
                role="radio"
                :tabindex="index === tabbableIndex ? 0 : -1"
                @click="selectLevel(level)"
                @keydown.down.prevent="moveSelection(index, 1)"
                @keydown.enter.prevent="selectLevel(level)"
                @keydown.left.prevent="moveSelection(index, -1)"
                @keydown.right.prevent="moveSelection(index, 1)"
                @keydown.space.prevent="selectLevel(level)"
                @keydown.up.prevent="moveSelection(index, -1)"
              >
                <v-icon
                  class="signature-level-card__radio"
                  :color="
                    level.key === signatureMode && !level.disabled
                      ? 'primary'
                      : 'grey-lighten-1'
                  "
                  size="20"
                >
                  {{
                    level.key === signatureMode
                      ? 'far fa-circle-dot'
                      : 'far fa-circle'
                  }}
                </v-icon>

                <div class="signature-level-card__content">
                  <div class="signature-level-card__heading">
                    <span class="signature-level-card__title">
                      {{ level.title }}
                    </span>
                    <span class="signature-level-card__tag">
                      {{ level.tag }}
                    </span>
                  </div>

                  <p class="signature-level-card__description">
                    {{ level.description }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </v-tooltip>
      </div>

      <HasNoPaymentMethod
        v-if="accountingQuery?.isFetched.value && !hasPaymentMethodSetup"
        :selected-organisation="selectedOrganization"
      />
    </section>

    <!-- Jurisdiction -->
    <section
      class="signature-mode-step__section signature-mode-step__section--jurisdiction"
      :class="{
        'expert-setting--visually-hidden': !showJurisdiction
      }"
    >
      <v-select
        v-model="jurisdictionModel"
        color="primary"
        data-test-id="jurisdiction-select"
        density="compact"
        hide-details
        :item-props="jurisdictionItemProps"
        item-title="label"
        item-value="key"
        :items="availableJurisdictions"
        :label="i18n.t('texts.infobar.jurisdiction')"
        variant="outlined"
      >
        <template #item="{ props: itemProps, internalItem }">
          <v-list-item v-bind="itemProps" :title="undefined">
            <template #prepend>
              <v-img
                v-if="jurisdictionByKey(internalItem.value)"
                :alt="jurisdictionByKey(internalItem.value)!.label"
                cover
                height="16"
                :src="`/svg/flags/${jurisdictionByKey(internalItem.value)!.flag}.svg`"
                width="24"
              />
            </template>
            <v-list-item-title
              class="font-weight-regular text-grey-darken-1 pl-2"
            >
              {{ jurisdictionByKey(internalItem.value)?.label }}
            </v-list-item-title>
          </v-list-item>
        </template>
        <template #selection>
          <div
            v-if="selectedJurisdiction"
            class="d-flex align-center"
            :data-test-id="selectedJurisdiction.key"
            :data-test-is-selected="true"
          >
            <v-img
              :alt="selectedJurisdiction.label"
              cover
              height="16"
              :src="`/svg/flags/${selectedJurisdiction.flag}.svg`"
              width="24"
            />
            <span class="font-weight-regular text-grey-darken-1 ml-2">
              {{ selectedJurisdiction.label }}
            </span>
          </div>
        </template>
      </v-select>
    </section>

    <!-- Additional settings: the expert-level knobs of this step, collapsed by
         default so the dialog leads with the signature level choice alone. -->
    <section
      v-if="canEnforceAuthorityService"
      ref="additionalSettingsSection"
      class="signature-mode-step__section signature-mode-step__section--additional"
    >
      <!--      <v-divider :style="{ marginRight: '-6px', marginLeft: '-6px' }" />-->
      <button
        :aria-expanded="showAdditionalSettings"
        class="additional-settings-toggle"
        data-test-id="additional-settings-toggle"
        type="button"
        @click="toggleAdditionalSettings"
      >
        <v-icon class="additional-settings-toggle__icon" size="14">
          {{
            showAdditionalSettings ? 'fas fa-caret-down' : 'fas fa-caret-right'
          }}
        </v-icon>
        <span class="additional-settings-toggle__label">{{
          i18n.t('titles.additional_settings')
        }}</span>
      </button>

      <!-- Shown/hidden outright, no expand transition: animating the height
           clips its contents mid-flight, which reads as the settings being cut
           off rather than revealed. The dialog body is a fixed-size scroll
           viewport, so an instant toggle shifts nothing around it. -->
      <div v-show="showAdditionalSettings" class="additional-settings-panel">
        <EnforceAuthorityServiceSetting />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watchEffect } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useOrganizationPageData } from '@/composables/useOrganizationData';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import { BetaFeature } from '@/types/enums/BetaFeature';
import AuthService from '@/services/authService';
import HasNoPaymentMethod from '@/components/cards/HasNoPaymentMethod.vue';
import EnforceAuthorityServiceSetting from '@/components/create/settings/EnforceAuthorityServiceSetting.vue';
import type { CompanySettings } from '@/types/deepsign/CompanySettings';

interface SignatureModeOption {
  key: SignatureMode;
  title: string;
  tag: string;
  description: string;
  dataTestId: string;
  disabled?: boolean;
}

interface JurisdictionOption {
  key: Jurisdiction;
  label: string;
  flag: string;
  disabled?: boolean;
}

const {
  signatureMode,
  jurisdiction,
  canEnforceAuthorityService,
  enforceAuthorityService,
  setSignatureMode,
  setJurisdiction
} = useSigningWorkflow();
const { currentDocument } = useDocumentContext();
const { accountingQuery, settingsQuery } = useOrganizationPageData();
const i18n = useI18n();
const deepAdminStore = useDeepAdminStore();

const jurisdictionModel = computed({
  get: () => jurisdiction.value,
  set: (value: Jurisdiction) => setJurisdiction(value)
});

function jurisdictionByKey(key: Jurisdiction): JurisdictionOption | undefined {
  return availableJurisdictions.value.find((j) => j.key === key);
}

function jurisdictionItemProps(item: JurisdictionOption) {
  return {
    disabled: !!item.disabled,
    'data-test-id': item.key,
    'data-test-is-selected': item.key === jurisdictionModel.value
  };
}

const settingsLoadedSuccessfully = computed(
  () => settingsQuery?.isSuccess.value
);
const settingsFailedToLoad = computed(() => settingsQuery?.isError.value);

const rawCompanySettings = computed<CompanySettings | undefined>(
  () => settingsQuery?.data.value
);

const companyAvailableSignatureModes = computed(() => {
  const settings = rawCompanySettings.value;
  const base = settings?.availableSignatureModes ?? [];
  const docMode = currentDocument.value?.signatureMode;
  if (docMode && !base.includes(docMode)) return [...base, docMode];
  return base;
});

const companyAvailableJurisdictions = computed(() => {
  const settings = rawCompanySettings.value;
  const base = settings?.availableJurisdictions ?? [];
  const docJurisdiction = currentDocument.value?.jurisdiction;
  if (docJurisdiction && !base.includes(docJurisdiction)) {
    return [...base, docJurisdiction];
  }
  return base;
});

const allSignatureModes = computed(() => [
  {
    title: i18n.t('titles.basic_signature'),
    tag: i18n.t('labels.signature_tag_ses'),
    description: i18n.t('texts.basic_short'),
    key: SignatureMode.TIMESTAMP,
    dataTestId: 'simple'
  },
  {
    title: i18n.t('titles.advanced_signature'),
    tag: i18n.t('labels.signature_tag_aes'),
    description: i18n.t('texts.advanced_short'),
    key: SignatureMode.ADVANCED,
    dataTestId: 'advanced'
  },
  {
    title: i18n.t('titles.qualified_signature'),
    tag: i18n.t('labels.signature_tag_qes'),
    description: i18n.t('texts.qualified_short'),
    key: SignatureMode.QUALIFIED,
    dataTestId: 'qualified'
  }
]);

const canUseDeepID = computed(
  () =>
    import.meta.env.VITE_AUTHORITY_SERVICE_ENABLED === 'true' ||
    AuthService.hasBetaFeature(BetaFeature.DEEP_ID_SIGN as never)
);

const selectedOrganization = computed(() =>
  deepAdminStore.userInfo?.companies.find(
    ({ group_id }) => group_id === currentDocument.value?.companyId
  )
);

const accountingData = computed(() => accountingQuery?.data.value);
const hasPaymentMethodSetup = computed<boolean | null>(() => {
  const d = accountingData.value;
  if (!d) return null;
  return !!d.has_payment_method;
});

const hasReachedFreeSesLimit = computed(() => {
  const d = accountingData.value;
  if (!d) return false;
  return !!d.has_reached_sign_ses_limit;
});

const availableSignatureModes = computed(() => {
  const allowedModes = companyAvailableSignatureModes.value;
  const docMode = currentDocument.value?.signatureMode;
  const settingsLoaded =
    settingsLoadedSuccessfully.value &&
    Array.isArray(allowedModes) &&
    allowedModes.length > 0;
  const hasPayment = hasPaymentMethodSetup.value;
  const accountingLoaded = !!accountingData.value;

  return allSignatureModes.value.map((mode) => {
    const allowBySettings = settingsLoaded
      ? allowedModes.includes(mode.key) || mode.key === docMode
      : false;
    const higherModeNeedsPayment =
      accountingLoaded &&
      (mode.key === SignatureMode.ADVANCED ||
        mode.key === SignatureMode.QUALIFIED) &&
      hasPayment === false;
    const sesBlockedByFreeLimit =
      accountingLoaded &&
      mode.key === SignatureMode.TIMESTAMP &&
      hasReachedFreeSesLimit.value &&
      hasPayment === false;
    const enabled =
      allowBySettings && !higherModeNeedsPayment && !sesBlockedByFreeLimit;
    return { ...mode, disabled: !enabled };
  });
});

const availableJurisdictions = computed(() => {
  const allowedJurisdictions = companyAvailableJurisdictions.value;
  const docJurisdiction = currentDocument.value?.jurisdiction;
  const settingsLoaded =
    Array.isArray(allowedJurisdictions) && allowedJurisdictions.length > 0;
  const fallbackAllowAll = settingsFailedToLoad.value;

  let base = [
    {
      key: Jurisdiction.ZERTES,
      label: i18n.t('certificates.ch'),
      flag: 'ch'
    },
    {
      key: Jurisdiction.EIDAS,
      label: i18n.t('certificates.eu'),
      flag: 'eu'
    }
  ];

  if (signatureMode.value === SignatureMode.ADVANCED && !canUseDeepID.value) {
    base = base.filter(
      (j) => j.key === Jurisdiction.ZERTES || j.key === docJurisdiction
    );
  }

  return base.map((j) => {
    const enabled = fallbackAllowAll
      ? true
      : settingsLoaded
        ? allowedJurisdictions.includes(j.key) || j.key === docJurisdiction
        : j.key === docJurisdiction;

    return { ...j, disabled: !enabled };
  });
});

const selectedJurisdiction = computed(() =>
  availableJurisdictions.value.find((j) => j.key === jurisdictionModel.value)
);

const additionalSettingsSection = ref<HTMLElement | null>(null);
const levelRefs = ref<(HTMLElement | null)[]>([]);

// Roving tabindex: exactly one card sits in the tab order — the selected one,
// or the first selectable one when the current selection is unavailable — so
// Tab enters and leaves the group once instead of stopping on every card.
const tabbableIndex = computed(() => {
  const modes = availableSignatureModes.value;
  const selected = modes.findIndex(
    (mode) => !mode.disabled && mode.key === signatureMode.value
  );
  if (selected !== -1) return selected;
  return modes.findIndex((mode) => !mode.disabled);
});

// Collapsed by default; opened up-front when the setting it holds is already
// switched on, so an active enforcement is never hidden behind a closed panel.
const showAdditionalSettings = ref(enforceAuthorityService.value);

const showJurisdiction = computed(
  () =>
    signatureMode.value !== SignatureMode.TIMESTAMP &&
    (settingsLoadedSuccessfully.value || settingsFailedToLoad.value)
);

function selectLevel(level: SignatureModeOption) {
  if (level.disabled) return;
  setSignatureMode(level.key);
}

// The panel sits at the bottom of a fixed-height, scrollable body, so bring it
// into view once it is in the DOM.
async function toggleAdditionalSettings() {
  showAdditionalSettings.value = !showAdditionalSettings.value;
  if (!showAdditionalSettings.value) return;

  await nextTick();
  additionalSettingsSection.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest'
  });
}

function setLevelRef(
  el: Element | ComponentPublicInstance | null,
  index: number
) {
  levelRefs.value[index] = (el as HTMLElement) ?? null;
}

// Arrow keys move the selection the way a native radio group does: unavailable
// levels are skipped and both ends wrap around.
function moveSelection(fromIndex: number, direction: 1 | -1) {
  const modes = availableSignatureModes.value;
  const count = modes.length;

  for (let offset = 1; offset <= count; offset++) {
    const index = (((fromIndex + direction * offset) % count) + count) % count;
    const candidate = modes[index];
    if (candidate.disabled) continue;
    selectLevel(candidate);
    levelRefs.value[index]?.focus();
    return;
  }
}

watchEffect(() => {
  const enabledJurisdictions = availableJurisdictions.value.filter(
    (j) => !j.disabled
  );
  if (!enabledJurisdictions.length) return;

  const current = jurisdiction.value;
  const isValid = enabledJurisdictions.some((j) => j.key === current);
  if (!isValid) {
    setJurisdiction(enabledJurisdictions[0].key);
  }
});
</script>

<style lang="scss" scoped>
.signature-mode-step {
  display: grid;
  gap: 24px;
}

.signature-mode-step__section {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.signature-mode-step__section--jurisdiction {
  min-height: 40px;
}

// Full-bleed: the divider and the hover band of the header row run the whole
// width of the dialog. The 16px inset comes from the scroll container in
// SigningWorkflowDialog, so negative margins of the same size reach its edges.
.signature-mode-step__section--additional {
  gap: 0;
  margin-inline: -16px;
}

.signature-level-list {
  display: grid;
  gap: 12px;
  // Equal-height cards. In an auto-height grid every `1fr` row resolves to the
  // tallest row's content, so the card whose description wraps to two lines
  // sets the height for all three instead of standing out as the tall one.
  grid-auto-rows: 1fr;
}

// The tooltip's activator wrapper sits between the grid and the card, so it has
// to hand the stretched row height down rather than shrink-wrap the card: as a
// grid container its own child stretches to fill it.
.signature-level-list__item {
  display: grid;
}

.signature-level-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  border-radius: 8px;
  background-color: rgb(var(--v-theme-surface));
  cursor: pointer;

  &:hover:not(.signature-level-card--disabled) {
    border-color: rgb(var(--v-theme-primary));
  }

  &--selected {
    border-color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.06);

    .signature-level-card__title {
      color: rgb(var(--v-theme-primary));
    }
  }

  // pointer-events pass through to the wrapper above so the "why is this
  // unavailable" tooltip still opens on hover.
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  // Keyboard users need to see which card the arrow keys act on.
  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  &__radio {
    flex: 0 0 auto;
    margin-top: 1px;
  }

  &__content {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  &__heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    color: rgb(var(--v-theme-grey-darken-4));
  }

  // Level acronym (SES / AES / QES) — a quiet chip so it reads as metadata
  // next to the title rather than a second heading.
  &__tag {
    padding: 2px 8px;
    border-radius: 4px;
    background-color: rgb(var(--v-theme-grey-lighten-4));
    color: rgb(var(--v-theme-grey-darken-2));
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: 0.02em;
  }

  &__description {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: rgb(var(--v-theme-grey-darken-1));
  }
}

// The whole row is the control, not just the label, so clicking anywhere along
// it toggles the panel.
.additional-settings-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: 0;
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  text-align: start;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }
}

// Back to the body inset — only the header row is full-bleed.
.additional-settings-panel {
  padding: 4px 16px 0;
}

.additional-settings-toggle__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
}

.additional-settings-toggle__label {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
</style>
